import { WIP_ANALYSIS_GRID_METRICS, type WipAnalysisGridMetricId } from "./wipMetric";

export type WipColumnSelection = {
	selectedMetricIds: ReadonlySet<WipAnalysisGridMetricId>;
	comparisonView: boolean;
};

export const ALL_WIP_ANALYSIS_GRID_METRIC_IDS: ReadonlySet<WipAnalysisGridMetricId> = new Set(
	WIP_ANALYSIS_GRID_METRICS.map((metric) => metric.id),
);

export const WIP_COMPARISON_VIEW_METRIC_IDS: ReadonlySet<WipAnalysisGridMetricId> = new Set([
	"contract",
	"est-gp",
	"gp-percent",
	"pct-comp",
	"underbillings",
	"overbillings",
	"pure-job-borrow",
]);

export const DEFAULT_WIP_COLUMN_SELECTION: WipColumnSelection = {
	selectedMetricIds: ALL_WIP_ANALYSIS_GRID_METRIC_IDS,
	comparisonView: false,
};

export const isWipColumnVisible = (
	selection: WipColumnSelection,
	metricId: WipAnalysisGridMetricId,
): boolean =>
	selection.comparisonView
		? WIP_COMPARISON_VIEW_METRIC_IDS.has(metricId)
		: selection.selectedMetricIds.has(metricId);

export const isWipColumnChecked = (
	selection: WipColumnSelection,
	metricId: WipAnalysisGridMetricId,
): boolean => selection.selectedMetricIds.has(metricId);

export const countWipVisibleColumns = (selection: WipColumnSelection): number =>
	WIP_ANALYSIS_GRID_METRICS.reduce(
		(count, metric) => count + (isWipColumnVisible(selection, metric.id) ? 1 : 0),
		0,
	);

/** Comparison View guarantees its preset, so the saved picks don't change while it's on. */
const updateSelectedMetricIds = (
	selection: WipColumnSelection,
	update: (
		selectedMetricIds: ReadonlySet<WipAnalysisGridMetricId>,
	) => ReadonlySet<WipAnalysisGridMetricId>,
): WipColumnSelection =>
	selection.comparisonView
		? selection
		: { ...selection, selectedMetricIds: update(selection.selectedMetricIds) };

export const toggleWipColumn = (
	selection: WipColumnSelection,
	metricId: WipAnalysisGridMetricId,
): WipColumnSelection =>
	updateSelectedMetricIds(selection, (selectedMetricIds) => {
		const next = new Set(selectedMetricIds);
		if (next.has(metricId)) next.delete(metricId);
		else next.add(metricId);
		return next;
	});

export const selectAllWipColumns = (selection: WipColumnSelection): WipColumnSelection =>
	updateSelectedMetricIds(selection, () => ALL_WIP_ANALYSIS_GRID_METRIC_IDS);

export const clearWipColumns = (selection: WipColumnSelection): WipColumnSelection =>
	updateSelectedMetricIds(selection, () => new Set());

export const setWipComparisonView = (
	selection: WipColumnSelection,
	comparisonView: boolean,
): WipColumnSelection => ({ ...selection, comparisonView });
